import type { CompletedHandRecord, MatchRepository, SavedMatch, StatsRepository } from '@texas42/app-model';
import type { WriteQueue } from './write-queue.js';
import { APP_VERSION, ENGINE_VERSION } from './constants.js';
import { STORAGE_SCHEMA_VERSION } from '@texas42/app-model';

export interface CreateSavedMatchInput {
  id: string;
  name: string;
  config: SavedMatch['config'];
  difficulty: SavedMatch['difficulty'];
  shuffleSeed?: number;
}

/**
 * Thin persistence helper for Phase 6 — queues immediate writes after accepted actions.
 */
export class MatchPersistenceCoordinator {
  private readonly listeners = new Set<(match: SavedMatch) => void>();

  subscribe(listener: (match: SavedMatch) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  constructor(
    private readonly matches: MatchRepository,
    private readonly stats: StatsRepository,
    private readonly queue: WriteQueue,
  ) {}

  getStatsRepository(): StatsRepository {
    return this.stats;
  }

  async createMatch(input: CreateSavedMatchInput): Promise<SavedMatch> {
    const now = new Date().toISOString();
    const config: SavedMatch['config'] = {
      ...input.config,
    };
    if (input.shuffleSeed !== undefined) {
      config.shuffleSeed = input.shuffleSeed;
    } else if (input.config.shuffleSeed !== undefined) {
      config.shuffleSeed = input.config.shuffleSeed;
    }

    const match: SavedMatch = {
      id: input.id,
      schemaVersion: STORAGE_SCHEMA_VERSION,
      appVersion: APP_VERSION,
      engineVersion: ENGINE_VERSION,
      name: input.name,
      status: 'in-progress',
      config,
      actionIds: [],
      createdAt: now,
      updatedAt: now,
      currentMarks: [0, 0],
      difficulty: input.difficulty,
    };

    await this.persistMatchUpdate(match);
    return match;
  }

  /** Save immediately after each accepted action update. */
  async persistMatchUpdate(match: SavedMatch): Promise<void> {
    const stamped: SavedMatch = {
      ...match,
      schemaVersion: STORAGE_SCHEMA_VERSION,
      appVersion: APP_VERSION,
      engineVersion: ENGINE_VERSION,
      updatedAt: new Date().toISOString(),
    };

    await this.matches.save(stamped);
    for (const listener of this.listeners) listener(stamped);
  }

  /** Persist match summary metadata and append completed-hand stats. */
  async persistHandCompletion(
    match: SavedMatch,
    hand: CompletedHandRecord,
  ): Promise<void> {
    await this.matches.save({
      ...match,
      schemaVersion: STORAGE_SCHEMA_VERSION,
      appVersion: APP_VERSION,
      engineVersion: ENGINE_VERSION,
      updatedAt: new Date().toISOString(),
    });
    await this.stats.appendCompletedHand(hand);
  }

  async flush(): Promise<void> {
    await this.queue.flush();
  }
}
